import { Router } from '@angular/router';
import { ChangeDetectionStrategy, ChangeDetectorRef, Component, HostListener, OnDestroy, OnInit } from '@angular/core';

import { Store } from '@ngrx/store';
import { combineLatest, Observable, map, switchMap, take } from 'rxjs';

import {
  ControlData,
  FinnairEmergencyContact,
  FinnairFrequentFlyerCardItem,
  FinnairOrder,
  FinnairPassengerItem,
} from '@fcom/dapi/api/models';
import { QualtricsSurveys } from '@fcom/common/qualtrics/qualtrics.utils';
import { CommonBookingFeatureState, orderData, orderDataPrimaryPassenger } from '@fcom/common-booking';
import { FeedbackService } from '@fcom/common/services/feedback/feedback.service';
import { ButtonTheme, ModalButtons } from '@fcom/ui-components';
import { LanguageService } from '@fcom/ui-translate';
import { TierBenefitService } from '@fcom/core/services/tier-benefit/tier-benefit.service';
import { TierLevelBenefits } from '@fcom/core/interfaces/tier-benefit.interface';
import { FrequentFlyerTierLevel } from '@fcom/common-booking/interfaces/utils.interface';
import { getCMSTier } from '@fcom/common-booking/utils/service.utils';

import { DeactivationGuarded } from '../services/passenger-details-deactivate.guard';

type PassengerTierLevel = Record<string, string>;

export type PassengerTierData = Record<
  string,
  {
    tierLabel: string;
    benefits: string[];
  }
>;

@Component({
  selector: 'fin-mmb-passenger-details-route',
  templateUrl: './passenger-details-route.component.html',
  styleUrls: ['../../../../common-booking/assets/helper-classes.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false,
})
export class ManageBookingPassengerDetailsRouteComponent implements DeactivationGuarded, OnInit, OnDestroy {
  readonly ModalButtons: typeof ModalButtons = ModalButtons;
  readonly ButtonTheme = ButtonTheme;

  unsavedChangesModalOpen = false;
  passengerInfosDirtyStatus: Record<string, boolean> = {};
  emergencyContactDirtyStatus = false;
  updatingPassengers: string[] = [];
  nextUrl: string;
  booking$: Observable<FinnairOrder>;
  controlData$: Observable<ControlData>;
  mainPaxInfo$: Observable<FinnairPassengerItem>;
  emergencyContact$: Observable<FinnairEmergencyContact>;
  allFrequentFlyerCards$: Observable<FinnairFrequentFlyerCardItem[]>;
  passengerTierLevel$: Observable<PassengerTierLevel>;
  tierBenefits$: Observable<TierLevelBenefits>;
  passengerTierData$: Observable<PassengerTierData>;

  constructor(
    private store$: Store<CommonBookingFeatureState>,
    private cdr: ChangeDetectorRef,
    private router: Router,
    private feedbackService: FeedbackService,
    private languageService: LanguageService,
    private tierBenefitService: TierBenefitService
  ) {}

  ngOnInit(): void {
    this.booking$ = this.store$.pipe(orderData());
    this.controlData$ = this.booking$.pipe(map((booking) => booking.controlData));
    this.mainPaxInfo$ = this.store$.pipe(orderDataPrimaryPassenger());
    this.emergencyContact$ = this.store$.pipe(
      orderData(),
      map((order) => order?.emergencyContact)
    );
    this.feedbackService.setSurvey(QualtricsSurveys.MMB_FEEDBACK);
    this.feedbackService.setFeedbackWidgetVisibility(true);
    this.allFrequentFlyerCards$ = this.booking$.pipe(
      map(({ passengers }) => passengers.flatMap((pax) => pax.frequentFlyerCards ?? []))
    );

    this.passengerTierLevel$ = this.booking$.pipe(
      map(({ passengers }) =>
        passengers
          .flatMap((pax) => pax)
          .reduce((acc, passenger) => {
            const frequentFlyerCard = passenger.frequentFlyerCards?.find((card) => card.companyCode === 'AY');
            if (frequentFlyerCard) {
              acc[passenger.id] = getCMSTier(frequentFlyerCard.tierLevel?.toString());
            }
            return acc;
          }, {} as PassengerTierLevel)
      )
    );
    this.tierBenefits$ = this.passengerTierLevel$.pipe(
      switchMap((passengerTierLevel) => {
        const tierLevels = Object.values(passengerTierLevel).join(',');
        return this.tierBenefitService.getBenefitJson(tierLevels, this.languageService.localeValue);
      })
    );

    this.passengerTierData$ = combineLatest([
      this.tierBenefits$,
      this.passengerTierLevel$,
      this.booking$.pipe(map(({ passengers }) => passengers)),
    ]).pipe(
      map(([tierBenefits, passengerTierLevel, passengers]) => {
        return passengers
          .filter((passenger) => this.hasFinnairPlusCard(passenger.frequentFlyerCards))
          .map((passenger) => {
            const passengerId = passenger.id;
            const benefits = this.tierBenefitService.filterBenefits(tierBenefits[passengerTierLevel[passengerId]]);
            return benefits
              ? {
                  [passengerId]: {
                    benefits: benefits.map((benefit) =>
                      benefit.description.generalDescription?.replace(/<[^>]*>/g, '')
                    ),
                    tierLabel: this.getTierLabel(passenger.frequentFlyerCards),
                  },
                }
              : null;
          })
          .filter((passengerData) => passengerData)
          .reduce((acc, passengerData) => Object.assign(acc, passengerData), {});
      })
    );
  }

  ngOnDestroy(): void {
    this.feedbackService.setFeedbackWidgetVisibility(false);
  }

  @HostListener('window:beforeunload')
  canUnload(): boolean {
    return !this.hasDirtyComponents();
  }

  private hasDirtyComponents(): boolean {
    return (
      this.emergencyContactDirtyStatus ||
      Object.keys(this.passengerInfosDirtyStatus)
        .map((key) => this.passengerInfosDirtyStatus[key])
        .some(Boolean)
    );
  }

  canDeactivate(nextUrl: string): boolean | Observable<boolean> | Promise<boolean> {
    if (this.hasDirtyComponents() && !this.unsavedChangesModalOpen) {
      this.nextUrl = nextUrl;
      this.unsavedChangesModalOpen = true;
      this.cdr.markForCheck();
      return false;
    } else {
      return true;
    }
  }

  hasFinnairPlusCard(cards: FinnairFrequentFlyerCardItem[]): boolean {
    return (cards || []).some((c) => c.companyCode && c.companyCode.toLowerCase() === 'ay');
  }

  getFinnairPlusTier(cards: FinnairFrequentFlyerCardItem[]): FrequentFlyerTierLevel {
    return (cards || []).find((c) => c.companyCode && c.companyCode.toLowerCase() === 'ay')
      ?.tierLevel as FrequentFlyerTierLevel;
  }

  getTierLabel(cards: FinnairFrequentFlyerCardItem[]): string {
    const tier = this.getFinnairPlusTier(cards);
    switch (tier) {
      case FrequentFlyerTierLevel.PLUS:
        return 'MMB.tierBenefits.basic.heading';
      case FrequentFlyerTierLevel.SILVER:
        return 'MMB.tierBenefits.silver.heading';
      case FrequentFlyerTierLevel.GOLD:
        return 'MMB.tierBenefits.gold.heading';
      case FrequentFlyerTierLevel.PLATINUM:
        return 'MMB.tierBenefits.platinum.heading';
      case FrequentFlyerTierLevel.LUMO:
        return 'MMB.tierBenefits.lumo.heading';
      case FrequentFlyerTierLevel.JUNIOR:
        return 'MMB.tierBenefits.junior.heading';
      default:
        return undefined;
    }
  }

  getAccompanyingInfant(pax: FinnairPassengerItem): Observable<FinnairPassengerItem> {
    return this.booking$.pipe(
      take(1),
      map((booking) => booking.passengers.find((p) => pax.id === p.accompanyingTravelerId))
    );
  }

  getAccompanyingAdult(pax: FinnairPassengerItem): Observable<FinnairPassengerItem> {
    return this.booking$.pipe(
      take(1),
      map((booking) => booking.passengers.find((p) => p.id === pax.accompanyingTravelerId))
    );
  }

  setPassengerDirtyStatus(passengerId: string, dirty: boolean): void {
    this.passengerInfosDirtyStatus[passengerId] = dirty;
  }

  setEmergencyContactDirtyStatus(dirty: boolean): void {
    this.emergencyContactDirtyStatus = dirty;
  }

  leaveWithoutSaving(): void {
    this.router.navigateByUrl(this.nextUrl);
  }

  continueEditing(): void {
    this.unsavedChangesModalOpen = false;
  }

  setUpdatingPassenger(id: string, updating: boolean): void {
    if (updating) {
      this.updatingPassengers.push(id);
    } else {
      this.updatingPassengers = this.updatingPassengers.filter((i) => i != id);
    }
  }
}
